import React, { useEffect, useState, Children } from "react";
import get from "lodash/get";
import isEmpty from "lodash/isEmpty";
import { Button, Modal, Form, Badge, Row, Col } from "react-bootstrap";
import { toast } from "react-toastify";
import { getApiPath, GENERIC_DATA_CREATE, GENERIC_DATA_EDIT, DYNAMIC_TABLE_ATTRIBUTE_LIST_BY_TABLE_ID_GET} from "../../../api/ApiConst";
import apiCaller from "../../../api/ApiCaller";
import {TABLE_ATTRIBUTE_TYPES} from "../../../settings/constants/types";
import DataTypeFactory from "../../../utils/datatypes/DataTypeFactory.jsx";
import ListComponent from "../../../utils/datatypes/ListComponent.jsx";
import "./AddEditTableRecord.css";

const AddEditTableRecordWithDataTypeFactory = ({
    isOpen = false,
    tablePathValue = "",
    fields = [],
    rowData = null,
    successCallBack,
    onClose,
    allTableList = []
}) => {
    const [formFields, setFormFields] = useState({});
    const [referenceTable, setReferenceTable] = useState(null);
    const [showAddMoreForm, setShowAddMoreForm] = useState(false);
    const [addMoreFieldName, setAddMoreFieldName] = useState(null);

    const actionType = rowData ? "Update" : "Create";

    const handleFieldChange = (fieldName) => (value) => {
        // For boolean values, use the value directly
        // For other values, extract from event object if needed
        const actualValue = typeof value === 'boolean' ? value : (value?.target?.value || value);
        console.log('Main handleFieldChange:', fieldName, 'value:', value, 'actualValue:', actualValue);
        setFormFields(prev => ({
            ...prev,
            [fieldName]: actualValue
        }));
    };

    const handleSuccess = () => {
        onClose();
        successCallBack();
    };

    const handleError = (error) => toast.error(error.response.data.message);

    const handleCreateUpdateTableData = () => {
        if (rowData) {
            const editURL = `${getApiPath(tablePathValue, GENERIC_DATA_EDIT)}/${rowData.id}`;
            apiCaller.callPutApi(editURL, formFields, {}, handleSuccess, handleError);
            return;
        }

        apiCaller.callPostApi(
            getApiPath(tablePathValue, GENERIC_DATA_CREATE),
            formFields,
            {},
            handleSuccess,
            handleError
        );
    };

    useEffect(() => {
        if (isOpen) {
            const initialFormFields = {};
            
            fields.forEach((field) => {
                let value;
                if (rowData) {
                    value = get(rowData, field.name);
                } else {
                    // For COLLECTION_OBJECT fields, set to null instead of empty string
                    if (field.type === TABLE_ATTRIBUTE_TYPES.COLLECTION_OBJECT) {
                        value = null;
                    } else if (field.type === TABLE_ATTRIBUTE_TYPES.BOOLEAN) {
                        value = false;
                    } else {
                        value = "";
                    }
                }
                initialFormFields[field.name] = value;
            });

            setFormFields(initialFormFields);
        }
    }, [isOpen, fields, rowData]);

    

    const renderField = (field, index) => {
        const { name, type, listAttribute } = field;
        const value = formFields[name];
        
        // Debug logging for Boolean fields
        if (type === TABLE_ATTRIBUTE_TYPES.BOOLEAN) {
            console.log('Rendering Boolean field:', name, 'value:', value, 'type:', typeof value);
        }

        // Handle list types with ListComponent
        if (listAttribute) {
            console.log('Main component - Rendering ListComponent for field:', name);
            console.log('Main component - Field type:', type);
            console.log('Main component - Value prop:', value);
            
            return (
                <Col lg={12} xl={12} key={index}>
                    <Form.Group className="mt-3">
                        <ListComponent
                            field={field}
                            value={value}
                            onChange={handleFieldChange(name)}
                        />
                    </Form.Group>
                </Col>
            );
        }

        // Special handling for Boolean fields to debug the issue
        if (type === TABLE_ATTRIBUTE_TYPES.BOOLEAN) {
            return (
                <Col lg={6} xl={4} key={index}>
                    <Form.Group className="mt-3">
                        <Form.Label>
                            {name} <Badge bg="info">{type}</Badge>
                        </Form.Label>
                        <div style={{ padding: '10px', border: '1px solid #ccc', borderRadius: '4px' }}>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <input 
                                    type="checkbox" 
                                    checked={Boolean(value)} 
                                    onChange={(e) => {
                                        console.log('Direct checkbox change:', e.target.checked);
                                        handleFieldChange(name)(e.target.checked);
                                    }}
                                    style={{ 
                                        cursor: 'pointer',
                                        width: '16px',
                                        height: '16px'
                                    }}
                                />
                                <span>Check this box</span>
                            </label>
                            <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                                Current value: {String(value)} (type: {typeof value})
                            </div>
                        </div>
                    </Form.Group>
                </Col>
            );
        }

        // Use DataTypeFactory for all single types (including OBJECT and COLLECTION_OBJECT)
        // OBJECT types get full width, others get smaller columns
        const columnSize = type === TABLE_ATTRIBUTE_TYPES.OBJECT ? 
            { lg: 12, xl: 12 } : 
            { lg: 6, xl: 4 };
        
        return (
            <Col {...columnSize} key={index}>
                <Form.Group className="mt-3">
                    <Form.Label>
                        {name} <Badge bg="info">{type}</Badge>
                    </Form.Label>
                    <DataTypeFactory
                        type={type}
                        value={value}
                        onChange={handleFieldChange(name)}
                        mode="edit"
                        config={{
                            ...field.config,
                            attributes: field.config?.attributes || field.attributes || []
                        }}
                        options={type === TABLE_ATTRIBUTE_TYPES.ENUM ? 
                            (field.config?.enumValues || []).map(val => ({ label: val, value: val })) : 
                            undefined
                        }
                        field={field}
                    />
                </Form.Group>
            </Col>
        );
    };

    return (
        <>
            <Modal show={isOpen} onHide={onClose} size="xl">
                <Modal.Header closeButton>
                    <Modal.Title>{`${actionType} ${tablePathValue} record`}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form className="mb-3">
                        <Row>
                            {fields.map((field, index) => renderField(field, index))}
                        </Row>
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        disabled={!tablePathValue || isEmpty(formFields)}
                        variant="primary"
                        onClick={handleCreateUpdateTableData}
                    >
                        {actionType}
                    </Button>
                </Modal.Footer>
            </Modal>

            {showAddMoreForm && (
                <AddMoreFormWithDataTypeFactory 
                    show={showAddMoreForm} 
                    setShow={setShowAddMoreForm} 
                    referenceTable={referenceTable} 
                    allTableList={allTableList} 
                    addMoreFieldName={addMoreFieldName}
                    setFormFields={setFormFields}
                    formFields={formFields}
                />
            )}
        </>
    );
};

// AddMoreForm with DataTypeFactory
const AddMoreFormWithDataTypeFactory = ({show, setShow, referenceTable, allTableList, addMoreFieldName, setFormFields, formFields}) => {
    const [addMoreFormFields, setAddMoreFormFields] = useState({});
    const [dynamicFormFields, setDynamicFormFields] = useState([]);

    useEffect(() => {
        if (show) {
            getTableAttributeListForTableColumns();
        }
    }, [show]);

    const getTableAttributeListForTableColumns = () => {
        var jsonRequest = {
            "page": 0,
            "size": 100,
            searchFilters: [],
            sortRules: [],
        };
        let attributesUrl = DYNAMIC_TABLE_ATTRIBUTE_LIST_BY_TABLE_ID_GET.replaceAll("{dynamic-table-id}", (referenceTable));
        const formOptions = [];
        apiCaller.callPostApi(attributesUrl, jsonRequest, {}, (response) => {
            response.data.map((d, i) => {
                if (d.formAttribute) {
                    let tablePathIs = allTableList.find(obj => obj.id === d.config?.referenceTable);
                    
                    formOptions.push({
                        name: d.name,
                        formAttribute: d.formAttribute,
                        type: d.type,
                        listAttribute: d.listAttribute,
                        config: d.config,
                        referenceTable: d.config?.referenceTable,
                        keyValuePair: { keyAttribute: d.config?.keyAttribute, valueAttribute: d.config?.valueAttribute },
                        referenceTablePath: (tablePathIs) ? tablePathIs?.pathValue : null,
                        enumValues: d.config?.enumValues
                    });
                }
            });
            setDynamicFormFields(formOptions);
            setAddMoreFormFields(
                Object.assign(
                    {},
                    ...formOptions.map((field) => {
                        const value = formFields?.[addMoreFieldName]?.[field.name];
                        
                        if (field.type === TABLE_ATTRIBUTE_TYPES.OBJECT) {
                            return {
                                [field.name]: value && typeof value === 'object' ? value : {}
                            };
                        } else if (field.type === TABLE_ATTRIBUTE_TYPES.COLLECTION_OBJECT && field.listAttribute) {
                            return {
                                [field.name]: Array.isArray(value) && value.length > 0 ? value : []
                            };
                        } else {
                            return {
                                [field.name]: value === 0 ? 0 :
                                value === false ? false :
                                value || ""
                            };
                        }
                    })
                )
            );
        }, (error) => {
            console.error(error);
        });
    };

    const handleFieldChange = (fieldName) => (value) => {
        // For boolean values, use the value directly
        // For other values, extract from event object if needed
        const actualValue = typeof value === 'boolean' ? value : (value?.target?.value || value);
        console.log('handleFieldChange actualValue:', actualValue);
        
        setAddMoreFormFields(prev => ({
            ...prev,
            [fieldName]: actualValue
        }));
    };

    const handleAddMoreForm = () => {
        if (!checkFormFields()) {
            return;
        }
        setFormFields((prev) => ({
            ...prev,
            [addMoreFieldName]: [...(formFields?.[addMoreFieldName] || []), {...(addMoreFormFields || {})}],
        }));
        setShow(false);
    };

    const checkFormFields = () => {
        let isFormValid = true;
        for (let key in addMoreFormFields) {
            const field = dynamicFormFields.find(f => f.name === key);
            const value = addMoreFormFields[key];
            
            if (field?.type === TABLE_ATTRIBUTE_TYPES.OBJECT) {
                continue;
            } else if (field?.type === TABLE_ATTRIBUTE_TYPES.BOOLEAN) {
                // Boolean fields are always valid (they can be true, false, or undefined)
                continue;
            } else {
                if ((typeof value === "string" && value?.trim() === "") || value === null || value === undefined) {
                    isFormValid = false;
                    toast.error(`${key} is required`);
                    break;
                }
            }
        }
        return isFormValid;
    };

    const renderField = (field, index) => {
        const { name, type, listAttribute } = field;
        const value = addMoreFormFields[name];

        // Handle list types with ListComponent
        if (listAttribute) {
            console.log('AddMoreForm - Rendering ListComponent for field:', name);
            console.log('AddMoreForm - Field type:', type);
            console.log('AddMoreForm - Value prop:', value);
            
            return (
                <Col lg={12} xl={12} key={index}>
                    <Form.Group className="mt-3">
                        <ListComponent
                            field={field}
                            value={value}
                            onChange={handleFieldChange(name)}
                        />
                    </Form.Group>
                </Col>
            );
        }

        // Use DataTypeFactory for all single types (including OBJECT and COLLECTION_OBJECT)
        // OBJECT types get full width, others get smaller columns
        const columnSize = type === TABLE_ATTRIBUTE_TYPES.OBJECT ? 
            { lg: 12, xl: 12 } : 
            { lg: 6, xl: 4 };
        
        return (
            <Col {...columnSize} key={index}>
                <Form.Group className="mt-3">
                    <Form.Label>
                        {name} <Badge bg="info">{type}</Badge>
                    </Form.Label>
                    <DataTypeFactory
                        type={type}
                        value={value}
                        onChange={handleFieldChange(name)}
                        mode="edit"
                        config={{
                            ...field.config,
                            attributes: field.config?.attributes || field.attributes || []
                        }}
                        options={type === TABLE_ATTRIBUTE_TYPES.ENUM ? 
                            (field.config?.enumValues || []).map(val => ({ label: val, value: val })) : 
                            undefined
                        }
                        field={field}
                    />
                </Form.Group>
            </Col>
        );
    };

    return (
        <Modal show={show} onHide={() => setShow(false)} className="add-more-form" size="xl">
            <Modal.Header closeButton>
                <Modal.Title>Add {referenceTable?.referenceTablePath}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Form className="mb-3">
                    <Row>
                        {dynamicFormFields.map((field, index) => renderField(field, index))}
                    </Row>
                </Form>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={() => setShow(false)}>
                    Close
                </Button>
                <Button variant="primary" onClick={handleAddMoreForm}>
                    Add
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default AddEditTableRecordWithDataTypeFactory; 